import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";

export type MiniSparklineProps = {
  /** Array of values, oldest first. Length should match the visible period (7 for "this week"). */
  values: number[];
  /** Delay before the path starts plotting (seconds). Used to cascade tiles. */
  delaySec?: number;
  /** Plot duration in ms. Default 1400 to match the main study-time chart. */
  durationMs?: number;
  /** Floor for the y-axis so a tiny single bump still shows a visible curve. */
  minDomain?: number;
  className?: string;
};

/**
 * Decorative mini-sparkline used inside dashboard stat tiles. No axes,
 * no grid, no tooltip -- just a soft brand-tinted monotone area whose
 * path plots in along its length via recharts' built-in animation.
 *
 * Same animation feel as the main StudyTimeChart for visual consistency
 * across the dashboard: the line "draws" out instead of just being
 * revealed by a clip-path mask.
 */
export function MiniSparkline({
  values,
  delaySec = 0.25,
  durationMs = 1400,
  minDomain = 1,
  className,
}: MiniSparklineProps) {
  const data = values.map((v, i) => ({ i, v: Math.max(0, Math.round(v)) }));
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="mini-spark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="rgb(var(--brand))" stopOpacity={0.18} />
              <stop offset="100%" stopColor="rgb(var(--brand))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[0, (max: number) => Math.max(minDomain, max * 1.2)]} />
          <Area
            type="monotone"
            dataKey="v"
            stroke="rgb(var(--brand))"
            strokeWidth={2}
            strokeLinecap="round"
            fill="url(#mini-spark)"
            dot={false}
            activeDot={false}
            // recharts' built-in path animation -- plots the curve in
            // along its length, matching the main study-time chart.
            isAnimationActive
            animationBegin={Math.round(delaySec * 1000)}
            animationDuration={durationMs}
            animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
